// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiConfig } from "../../apiConfig";
import { getHeaders, ApiScope } from "../silentAuth";
import {
  AddResourceGroupToTenantCommand,
  AddUserToTenantCommand,
  CreateTenantCommand,
  RemoveResourceGroupFromTenantCommand,
  RemoveUserFromTenantCommand,
} from "./types";
import { ICompany } from "@oqdo/automationapps/Agents/Organizations/Company";
import { PagedResult } from "../types";

// Define a service using a base URL and expected endpoints
export const tenantAPi = createApi({
  reducerPath: "tenantAPi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiConfig.tenantAPiUrl,
    prepareHeaders: async (headers, { getState }) => {
      return await getHeaders(ApiScope, headers);
    },
  }),
  tagTypes: ["Tenants"],
  endpoints: (builder) => ({
    getTenantById: builder.query<ICompany, string>({
      query: (id) => `/${id}`,
      providesTags: ["Tenants"],
    }),
    getTenants: builder.query<PagedResult<ICompany>, string | undefined>({
      query: (subscriptionId) => {
        return subscriptionId !== undefined ? `?subscription=${subscriptionId}` : `/`;
      },
      providesTags: ["Tenants"],
    }),
    getTenantsResourceGroup: builder.query<ICompany[], string>({
      query: (id) => `/${id}/resourceGroups`,
      providesTags: ["Tenants"],
    }),
    createTenant: builder.mutation<any, CreateTenantCommand>({
      query: (obj) => ({
        url: `/`,
        method: "post",
        body: obj,
        responseHandler: (response: { text: () => any }) => response.text(),
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(tenantAPi.util.invalidateTags(["Tenants"]));
        }, 3000);
      },
      invalidatesTags: ["Tenants"],
    }),
    removeResourceGroup: builder.mutation<any, RemoveResourceGroupFromTenantCommand>({
      //"tenants/{id}/resourceGroups/{resourceGroupId}"
      query: (args) => ({
        url: `/` + args.tenantId + "/resourceGroups/" + args.resourceGroupId,
        method: "delete",
      }),
      invalidatesTags: ["Tenants"],
    }),
    addResourceGroup: builder.mutation<any, AddResourceGroupToTenantCommand>({
      //"tenants/{id}/resourceGroups/{resourceGroupId}"
      query: (args) => ({
        url: `/` + args.tenantId + "/resourceGroups/",
        method: "post",
        body: args,
      }),
      invalidatesTags: ["Tenants"],
    }),
    addUserToTenant: builder.mutation<any, AddUserToTenantCommand>({
      query: (args) => ({
        url: `/` + args.tenantId + "/users",
        method: "post",
        body: args,
      }),
      invalidatesTags: ["Tenants"],
    }),
    removeUserFromTenant: builder.mutation<any, RemoveUserFromTenantCommand>({
      query: (args) => ({
        url: `/` + args.tenantId + "/users/" + args.userId,
        method: "delete",
      }),
      invalidatesTags: ["Tenants"],
    }),
    deleteTenant: builder.mutation<void, string>({
      query: (id) => ({
        url: `/` + id,
        method: "delete",
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(tenantAPi.util.invalidateTags(["Tenants"]));
        }, 3000);
      },
      invalidatesTags: ["Tenants"],
    }),
    updateTenant: builder.mutation<any, { id: string; data: Partial<ICompany> }>({
      query: (args) => ({
        url: `/` + args.id,
        method: "patch",
        body: args.data,
      }),
      invalidatesTags: ["Tenants"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetTenantByIdQuery,
  useGetTenantsQuery,
  useRemoveResourceGroupMutation,
  useAddResourceGroupMutation,
  useAddUserToTenantMutation,
  useRemoveUserFromTenantMutation,
  useCreateTenantMutation,
  useGetTenantsResourceGroupQuery,
  useDeleteTenantMutation,
  useUpdateTenantMutation,
} = tenantAPi;
